package com.essntl.features.request.data.repository

import com.essntl.core.supabase.*
import com.essntl.core.utils.repository.Repository
import com.essntl.features.request.data.supabase.RequestDto
import com.essntl.features.request.data.supabase.RequestSupabaseDataSource
import com.essntl.features.request.data.supabase.toDto
import com.essntl.features.request.data.supabase.toRequestMap
import com.essntl.features.request.domain.model.RequestFilterOptions
import com.essntl.features.request.domain.model.RequestModel
import com.essntl.features.request.domain.repository.RequestRepository
import io.github.jan.supabase.postgrest.query.Order
import io.github.jan.supabase.postgrest.query.filter.PostgrestFilterBuilder
import org.koin.core.annotation.Single

@Single(binds = [RequestRepository::class])
internal class RequestRepositoryImpl internal constructor(
    private val supabase: RequestSupabaseDataSource,
) : Repository(), RequestRepository {
    override suspend fun getById(id: String): Result<RequestModel> =
        runCatching {
            supabase
                .getById(id = id, columns = RequestDto.getColumns())
                .toModel()
        }

    override suspend fun getRequestsByClientId(clientId: String): Result<List<RequestModel>> =
        runCatching {
            supabase
                .getAll(
                    columns = RequestDto.getColumns(),
                ) {
                    filter {
                        cs("clients", listOf(clientId))
                    }
                }
                .map {
                    it.toModel()
                }
        }

    override suspend fun getRequestsByTravelAgentId(travelAgentId: String): Result<List<RequestModel>> =
        runCatching {
            supabase
                .getAll(
                    columns = RequestDto.getColumns(),
                ) {
                    filter {
                        eq("travel_agent", travelAgentId)
                    }
                }
                .map {
                    it.toModel()
                }
        }


    override suspend fun getAllByProposalIds(proposalIds: List<String>): Result<List<RequestModel>> =
        runCatching {
            supabase
                .getAll(
                    columns = RequestDto.getColumns(),
                ) {
                    filter {
                        or {
                            proposalIds.forEach { proposalId ->
                                eq("proposal_id", proposalId)
                            }
                        }
                    }
                }
                .map {
                    it.toModel()
                }
        }

    override suspend fun getAll(): Result<List<RequestModel>> =
        runCatching {
            supabase
                .getAll(
                    columns = RequestDto.getColumns(),
                )
                .map {
                    it.toModel()
                }
        }

    override suspend fun getAll(
        page: Long,
        limit: Int,
        filterOptions: RequestFilterOptions,
    ): Result<List<RequestModel>> =
        runCatching(sendToastOnFailure = false) {
            supabase
                .getAll(
                    columns = RequestDto.getColumns(),
                    page = page,
                    limit = limit,
                ) {
                    if (
                        filterOptions.searchText.isNotEmpty() ||
                        filterOptions.selectedProfileIds.isNotEmpty() ||
                        filterOptions.selectedRequestStatus != null
                    ) {
                        filter {
                            applyRequestFilter(filterOptions)
                        }
                    } else {
                        filter {
                            neq("status", "ARCHIVED")
                        }
                    }
                    order(
                        column = "created_at",
                        order = Order.DESCENDING,
                    )
                }
                .map { it.toModel() }
        }

    private fun PostgrestFilterBuilder.applyRequestFilter(filterOptions: RequestFilterOptions) {
        if (filterOptions.searchText.isNotEmpty()) {
            ilike("title", "%${filterOptions.searchText}%")
        }

        if (filterOptions.selectedProfileIds.isNotEmpty()) {
            and {
                or {
                    filterOptions.selectedProfileIds.forEach { profileId ->
                        eq("owner_id", profileId)
                    }
                }
            }
        }

        if (filterOptions.selectedRequestStatus != null) {
            eq("status", filterOptions.selectedRequestStatus!!.toDto())
        }
    }

    override suspend fun getAllByStatus(status: RequestModel.Status): Result<List<RequestModel>> =
        runCatching {
            supabase
                .getAll(
                    columns = RequestDto.getColumns(),
                ) {
                    filter {
                        eq("status", status.toDto())
                    }
                }
                .map {
                    it.toModel()
                }
        }

    override suspend fun insert(request: RequestModel): Result<RequestModel> =
        runCatching {
            val requestMap = request.toRequestMap()
            supabase
                .insert(
                    requestMap = requestMap,
                    columns = RequestDto.getColumns(),
                ).toModel()
        }

    override suspend fun update(request: RequestModel): Result<RequestModel> =
        runCatching {
            val requestMap = request.toRequestMap()
            supabase
                .update(
                    id = request.id,
                    requestMap = requestMap,
                    columns = RequestDto.getColumns(),
                ).toModel()
        }

    override suspend fun delete(request: RequestModel): Result<Unit> =
        runCatching {
            supabase.delete(id = request.id)
        }
}
